import { api } from "@/lib/api";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

export const GCAL_NOT_CONNECTED = "GCAL_NOT_CONNECTED";

/**
 * SWR cache keys, in one place so writers and readers cannot drift apart.
 *
 * The grid key is scoped to a month window, so anything outside the calendar
 * cannot rebuild it. Match those with a predicate instead:
 *   mutate((k) => isGcalEventsKey(k))
 */
export const gcalStatusKey = ["gcal-status"];
export const gcalEventKey = (eventId: string) => ["gcal-event", eventId];
export const gcalEventsKey = (windowKey: string) => ["gcal-events", windowKey];
export const isGcalEventsKey = (key: unknown) =>
	Array.isArray(key) && key[0] === "gcal-events";

export interface TaskCalendarEvent {
	google_event_id: string;
	html_link: string | null;
}

/**
 * An event on the user's primary Google calendar, trimmed server-side.
 * `start`/`end` are "YYYY-MM-DD" when `allDay`, RFC3339 with an offset otherwise.
 */
export interface GoogleEvent {
	id: string;
	summary: string | null;
	htmlLink: string | null;
	start: string | null;
	end: string | null;
	allDay: boolean;
	/** "default" | "outOfOffice" | "focusTime"; drives the block colour. */
	eventType: string;
	/**
	 * False for meetings someone else organized without guest-modify, and for
	 * locked events. Google rejects patches on those, so the grid hides the
	 * resize handle rather than letting a drag fail on drop.
	 */
	canEdit: boolean;
	/** One occurrence of a series. Edits here affect this occurrence only. */
	isRecurring: boolean;
	/**
	 * The authenticated user's RSVP status for this event.
	 * "accepted" | "declined" | "tentative" | "needsAction".
	 * Events the user owns default to "accepted".
	 */
	responseStatus: string;
	organizerIsSelf?: boolean;
}

/** What `events.get` adds on top of the trimmed list shape. */
export interface GoogleEventDetail extends GoogleEvent {
	description: string | null;
	location: string | null;
	organizer: string | null;
	linkedTask?: { id: string; title: string; logId: string; projectId: string };
}

export interface UpdateCalendarEventPayload {
	title?: string;
	/** ISO date-times; send both or neither. */
	start?: string;
	end?: string;
	description?: string;
	eventType?: CalendarEventType;
	/** Email the organizer and guests about this change. Defaults to false. */
	notify?: boolean;
}

/**
 * The event types Google accepts on events.insert for the primary calendar.
 * The Calendar UI also offers Task and Appointment schedule; a task is a Google
 * Tasks resource on a separate API and scope, and appointment schedules have no
 * public API, so neither can be created here.
 */
export const CALENDAR_EVENT_TYPES = [
	{ value: "default", label: "Event" },
	{ value: "outOfOffice", label: "Out of office" },
	{ value: "focusTime", label: "Focus time" },
] as const;

export type CalendarEventType = (typeof CALENDAR_EVENT_TYPES)[number]["value"];

/** Display name for an event type, falling back for types added since. */
export function calendarTypeLabel(eventType: string): string {
	return (
		CALENDAR_EVENT_TYPES.find((t) => t.value === eventType)?.label ?? "Event"
	);
}

export interface CreateStandaloneEventPayload {
	title: string;
	/** ISO date-times. */
	start: string;
	end: string;
	description?: string;
	eventType: CalendarEventType;
}

export interface CreateCalendarEventPayload {
	start: string; // ISO date-time
	durationMin: number;
	title: string;
}

export async function getCalendarStatus(): Promise<{ connected: boolean }> {
	const res = await api.get<{ success: boolean; data: { connected: boolean } }>(
		"/integrations/google-calendar/status",
	);
	return res.data;
}

/**
 * @param returnTo App path to land on after consent. Must be a same-origin
 * absolute path; the server falls back to /tasks for anything else.
 */
export async function startCalendarConnect(returnTo?: string): Promise<string> {
	const res = await api.post<{ success: boolean; data: { url: string } }>(
		"/integrations/google-calendar/connect",
		returnTo ? { returnTo } : {},
	);
	return res.data.url;
}

/** Revokes the Google grant and clears the stored connection. */
export async function disconnectCalendar(): Promise<void> {
	await api.delete("/integrations/google-calendar/connection");
}

export async function getTaskCalendarEvent(
	projectId: string,
	taskId: string,
): Promise<TaskCalendarEvent | null> {
	const res = await api.get<{
		success: boolean;
		data: TaskCalendarEvent | null;
	}>(`/projects/${projectId}/tasks/${taskId}/calendar-event`);
	return res.data;
}

export async function createTaskCalendarEvent(
	projectId: string,
	taskId: string,
	payload: CreateCalendarEventPayload,
): Promise<TaskCalendarEvent> {
	const res = await api.post<{ success: boolean; data: TaskCalendarEvent }>(
		`/projects/${projectId}/tasks/${taskId}/calendar-event`,
		payload,
	);
	return res.data;
}

export async function listCalendarEvents(
	timeMin: string,
	timeMax: string,
): Promise<GoogleEvent[]> {
	const query = new URLSearchParams({ timeMin, timeMax });
	const res = await api.get<{
		success: boolean;
		data: { events: GoogleEvent[] };
	}>(`/integrations/google-calendar/events?${query.toString()}`);
	return res.data.events;
}

export async function createCalendarEvent(
	payload: CreateStandaloneEventPayload,
): Promise<{ id: string; htmlLink: string | null }> {
	const res = await api.post<{
		success: boolean;
		data: { id: string; htmlLink: string | null };
	}>("/integrations/google-calendar/events", payload);
	return res.data;
}

export async function getCalendarEvent(
	eventId: string,
): Promise<GoogleEventDetail> {
	const res = await api.get<{ success: boolean; data: GoogleEventDetail }>(
		`/integrations/google-calendar/events/${encodeURIComponent(eventId)}`,
	);
	return res.data;
}

export async function updateCalendarEvent(
	eventId: string,
	payload: UpdateCalendarEventPayload,
): Promise<GoogleEventDetail> {
	const res = await api.patch<{ success: boolean; data: GoogleEventDetail }>(
		`/integrations/google-calendar/events/${encodeURIComponent(eventId)}`,
		payload,
	);
	return res.data;
}

/**
 * Returns the trimmed list shape, not the detail shape: the server responds with
 * `toClientEvent(patched)`, which carries no description, location, organizer or
 * linked task. Merge it into a cached detail, never overwrite one with it.
 */
export async function updateCalendarEventRSVP(
	eventId: string,
	responseStatus: "accepted" | "declined" | "tentative",
	notify = false,
): Promise<GoogleEvent> {
	const res = await api.patch<{ success: boolean; data: GoogleEvent }>(
		`/integrations/google-calendar/events/${encodeURIComponent(eventId)}/rsvp`,
		{ responseStatus, notify },
	);
	return res.data;
}

export { API_URL as GCAL_API_URL };
